import { FINAL_UPLOAD_COLUMNS } from '@erp/shared-types';
import type { UploadLayout, UploadLayoutDefinition } from '@erp/rpc';

import { FEEDBACK_NEW_MARGIN, feedbackLayoutColumns } from './feedbackLayout';
import { BUILT_IN_LAYOUT_EPOCH } from './supplierLayouts';

export const HUBBELL_LAYOUT_NAME = 'Hubbell';

/**
 * FINAL.xlsx's last 82 columns (W…CZ, after Division and LOADED TO): the 82
 * upload columns in order, fourteen under FINAL's own names. The layout reads
 * each by FINAL's header at that position.
 */
export const HUBBELL_FINAL_UPLOAD_HEADERS = FINAL_UPLOAD_COLUMNS;

// Both Feedback increases are in points for Hubbell (1 = 1%), unlike
// Stelpro's Core Cost Increase, which is a fraction.
const NEW_COST_ADDER =
  '[FINAL PRIOR COST ADDER] + DEFAULT([Feedback: Core Cost Increase], 0) / 100';
const NEW_MARGIN = FEEDBACK_NEW_MARGIN;

/**
 * The Hubbell layout: one Division tab of FINAL.xlsx with that Division's
 * filled-in Feedback File (first sheet "<Division code> Feedback", e.g. "QO
 * Feedback" or "WG Feedback", matched on LOAD NUM =
 * Num). Everything passes through from FINAL by position except:
 *
 * - SELL NET: QUOTE COST PER EACH (Hubbell's new cost) × (1 + new cost adder)
 *   ÷ Pricing Factor ÷ (1 − new margin), to 2 decimals. Pricing Factor 0.01
 *   prices per hundred. At prior cost with no increases this reproduces
 *   FINAL's PRIOR SELL NET.
 * - COST ADD FIELD: the new cost adder in points; blank when there is none.
 * - COST ADD FLAG: FINAL's CHGDCCAD, cleared when COST ADD FIELD is blank.
 *
 * New margin = FINAL PRIOR MARGINS + Margin Increase / 100; new cost adder =
 * FINAL PRIOR COST ADDER + Core Cost Increase / 100. A blank increase is none.
 * Rows with no AS400 Item, a blank Pricing Factor (not in the system), a
 * blank FINAL PRIOR MARGINS (FINAL's promo loads carry none, and a sell price
 * is never made up at no margin) or a quote cost of 0 are left out. The
 * pricing team's answers behind these rules are recorded on AIA-28/AIA-29.
 */
export const HUBBELL_LAYOUT: UploadLayoutDefinition = {
  includeWhenAnyFilled: ['AS400 Item'],
  skipWhenBlank: ['Pricing Factor', 'FINAL PRIOR MARGINS'],
  skipWhenZero: ['QUOTE COST PER EACH'],
  lookup: { label: 'Feedback', matchColumn: 'LOAD NUM', keyColumn: 'Num' },
  columns: feedbackLayoutColumns({
    headers: HUBBELL_FINAL_UPLOAD_HEADERS,
    sellNet: `[QUOTE COST PER EACH] * (1 + ${NEW_COST_ADDER}) / [Pricing Factor] / (1 - (${NEW_MARGIN}))`,
    newCostAdder: NEW_COST_ADDER,
  }),
};

export const BUILT_IN_HUBBELL_LAYOUT: UploadLayout = {
  id: '00000000-0000-4000-8000-000000000003',
  name: HUBBELL_LAYOUT_NAME,
  definition: HUBBELL_LAYOUT,
  createdAt: BUILT_IN_LAYOUT_EPOCH,
  updatedAt: BUILT_IN_LAYOUT_EPOCH,
};
